import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, UserX, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { ChalkCard, DarkHeader, SectionTitle } from "../../components/chalk/primitives";

export default function AdminOverview() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState("");
  const apiBase = (typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL) || "";

  async function load() {
    try {
      const [d, l, u, f] = await Promise.all([
        api.adminOverview(),
        api.adminListLeads(),
        api.adminListUsers(),
        api.adminFunnel(30),
      ]);
      setData(d); setLeads(l.leads || []); setUsers(u.users || []); setFunnel(f);
    } catch (e) {
      toast.error(e.message || "Couldn't load admin data.");
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function withReload(fn) {
    try { await fn(); await load(); } catch (e) { toast.error(e.message); }
  }

  return (
    <>
      <DarkHeader
        eyebrow="Admin"
        title="Operational dashboard"
        subtitle="Reuses existing auth, audit, billing, and queue systems."
        right={
          <button onClick={() => navigate(-1)} aria-label="Back" style={backBtn}>
            <ArrowLeft size={14} /> Back
          </button>
        }
      />
      <div style={{ padding: "0 18px 32px", maxWidth: 1080, margin: "0 auto" }}>
        <div style={{ display: "flex", gap: 6, marginTop: 14, flexWrap: "wrap" }} data-testid="admin-overview-tabs">
          {[
            ["overview", "Overview"], ["users", "Users"], ["subscriptions", "Subscriptions"],
            ["leads", "Marketing leads"], ["billing", "Billing health"],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
                    data-testid={`tab-${k}`}
                    style={{
                      background: tab === k ? "var(--chalk-board)" : "#fff",
                      color: tab === k ? "var(--chalk-white)" : "var(--chalk-board)",
                      border: "0.5px solid var(--chalk-border)",
                      padding: "6px 14px", borderRadius: 999, fontSize: 13, cursor: "pointer",
                    }}>{label}</button>
          ))}
          <Link to="/admin/jobs" style={tabLinkBtn}>Jobs</Link>
          <Link to="/admin/audit" style={tabLinkBtn}>Audit</Link>
          <Link to="/admin/permission-tests" style={tabLinkBtn}>Permission tests</Link>
        </div>

        {!data ? (
          <div style={{ padding: 24, color: "var(--chalk-muted)" }}>Loading…</div>
        ) : tab === "overview" ? (
          <OverviewTab data={data} funnel={funnel} />
        ) : tab === "users" ? (
          <UsersTab users={users} busy={busy} setBusy={setBusy} withReload={withReload} />
        ) : tab === "subscriptions" ? (
          <SubsTab users={users} data={data} />
        ) : tab === "leads" ? (
          <LeadsTab leads={leads} apiBase={apiBase} />
        ) : (
          <BillingTab data={data} />
        )}
      </div>
    </>
  );
}

function OverviewTab({ data, funnel }) {
  const t = data.totals, s = data.subscriptions, g = data.growth;
  return (
    <>
      <SectionTitle label="Conversion funnel · last 30 days" />
      <FunnelWidget funnel={funnel} />

      <SectionTitle label="Totals" />
      <div style={statGrid}>
        <Stat label="Users" value={t.users} />
        <Stat label="Coaches" value={t.coaches} />
        <Stat label="Guardians" value={t.guardians} />
        <Stat label="Leagues" value={t.leagues} />
        <Stat label="Teams" value={t.teams} />
        <Stat label="Active teams" value={t.active_teams} />
      </div>
      <SectionTitle label="Subscriptions" />
      <div style={statGrid}>
        <Stat label="Active Chalkboard" value={s.active_chalkboard} />
        <Stat label="Active Clipboard+" value={s.active_clipboard_plus} />
        <Stat label="Trialing" value={s.trialing} />
        <Stat label="Past due" value={s.past_due} warn />
        <Stat label="Cancelled (30d)" value={s.cancelled_last_30d} />
      </div>
      <SectionTitle label="Growth" />
      <div style={statGrid}>
        <Stat label="Signups (7d)" value={g.signups_last_7d} />
        <Stat label="Signups (30d)" value={g.signups_last_30d} />
        <Stat label="Leads (7d)" value={g.leads_last_7d} />
        <Stat label="Leads (total)" value={g.leads_total} />
        <Stat label="Upgrades (30d)" value={g.upgrades_last_30d} />
      </div>
      <SectionTitle label="Recent signups" />
      {data.recent_signups.length === 0 ? (
        <ChalkCard><div style={{ color: "var(--chalk-muted)", fontSize: 13 }}>No signups yet.</div></ChalkCard>
      ) : data.recent_signups.map((u) => (
        <ChalkCard key={u.id}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{u.first_name || "(unnamed)"}</div>
              <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>{u.role} · {new Date(u.created_at).toLocaleString()}</div>
            </div>
          </div>
        </ChalkCard>
      ))}
    </>
  );
}

function UsersTab({ users, busy, setBusy, withReload }) {
  return (
    <>
      <SectionTitle label={`${users.length} users`} />
      {users.map((u) => (
        <ChalkCard key={u.id} data-testid={`user-row-${u.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: u.deleted_at ? "var(--chalk-muted)" : "var(--chalk-board)" }}>
                {u.first_name} {u.last_name} {u.deleted_at && "· DELETED"} {u.disabled && !u.deleted_at && "· DISABLED"}
              </div>
              <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>
                {u.email} · {u.role} · {u.tier}{u.sub_status ? ` (${u.sub_status})` : ""} · {u.teams_count} team{u.teams_count === 1 ? "" : "s"}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              {u.deleted_at ? (
                <SmallBtn testId={`restore-${u.id}`} onClick={() => withReload(() => api.adminRestoreUser(u.id))} icon={<Undo2 size={12} />}>Restore</SmallBtn>
              ) : (
                <>
                  <SmallBtn testId={`disable-${u.id}`}
                            onClick={() => withReload(() => api.adminDisableUser(u.id, !u.disabled))}
                            icon={<UserX size={12} />}>
                    {u.disabled ? "Enable" : "Disable"}
                  </SmallBtn>
                  <SmallBtn testId={`delete-${u.id}`} danger
                            onClick={() => {
                              if (!window.confirm(`Soft-delete ${u.email}?`)) return;
                              withReload(() => api.adminDeleteUser(u.id));
                            }}
                            icon={<Trash2 size={12} />}>Delete</SmallBtn>
                </>
              )}
            </div>
          </div>
        </ChalkCard>
      ))}
    </>
  );
}

function SubsTab({ users }) {
  const paid = users.filter((u) => u.sub_status === "active" || u.sub_status === "trialing");
  return (
    <>
      <SectionTitle label={`${paid.length} active subscriptions`} />
      {paid.length === 0 ? (
        <ChalkCard><div style={{ color: "var(--chalk-muted)", fontSize: 13 }}>No paid subscribers yet.</div></ChalkCard>
      ) : paid.map((u) => (
        <ChalkCard key={u.id}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{u.first_name} {u.last_name}</div>
              <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>{u.email}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: "var(--chalk-green)" }}>{u.tier}</div>
              <div style={{ fontSize: 11, color: "var(--chalk-muted)" }}>{u.sub_status}</div>
            </div>
          </div>
        </ChalkCard>
      ))}
    </>
  );
}

function LeadsTab({ leads, apiBase }) {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18 }}>
        <SectionTitle label={`${leads.length} marketing leads`} />
        <a href={`${apiBase}/api/v1/admin/marketing/leads.csv`}
           data-testid="leads-csv-export"
           style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--chalk-board)", color: "var(--chalk-white)", padding: "6px 12px", borderRadius: 999, textDecoration: "none", fontSize: 12 }}>
          <Download size={12} /> Export CSV
        </a>
      </div>
      {leads.length === 0 ? (
        <ChalkCard><div style={{ color: "var(--chalk-muted)", fontSize: 13 }}>No leads yet. Share your landing page.</div></ChalkCard>
      ) : leads.map((l) => (
        <ChalkCard key={l.id} data-testid={`lead-row-${l.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{l.first_name}</div>
              <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>{l.email} · {l.source}</div>
            </div>
            <div style={{ fontSize: 11, color: "var(--chalk-muted)" }}>{new Date(l.created_at).toLocaleString()}</div>
          </div>
        </ChalkCard>
      ))}
    </>
  );
}

function BillingTab({ data }) {
  const s = data.subscriptions;
  return (
    <>
      <SectionTitle label="Billing health" />
      <div style={statGrid}>
        <Stat label="Past due" value={s.past_due} warn />
        <Stat label="Cancelled (30d)" value={s.cancelled_last_30d} />
        <Stat label="Trialing" value={s.trialing} />
      </div>
      <ChalkCard>
        <div style={{ fontSize: 13, color: "var(--chalk-muted)", lineHeight: 1.6 }}>
          Webhook health, Stripe events, and notification logs are linked from the existing admin tabs:
          <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Link to="/admin/billing" style={miniLink}>Billing controls</Link>
            <Link to="/admin/notifications" style={miniLink}>Notifications</Link>
            <Link to="/admin/emails" style={miniLink}>Emails</Link>
            <Link to="/admin/audit" style={miniLink}>Audit log</Link>
          </div>
        </div>
      </ChalkCard>
    </>
  );
}

function FunnelWidget({ funnel }) {
  if (!funnel) {
    return (
      <ChalkCard data-testid="funnel-widget-loading">
        <div style={{ color: "var(--chalk-muted)", fontSize: 13 }}>Loading funnel…</div>
      </ChalkCard>
    );
  }
  const { signups, checkout_starts, upgrades, conversion } = funnel;
  // Bar widths are proportional to the top stage so the shape of the funnel
  // is immediately readable.
  const top = Math.max(signups.total, checkout_starts.total, upgrades.total, 1);
  const w = (n) => `${Math.max(6, (n / top) * 100)}%`;

  const stages = [
    {
      key: "signups",
      label: "Signups",
      total: signups.total,
      sub: `${signups.coach} coach · ${signups.guardian} guardian`,
      pct: null,
      color: "var(--chalk-board)",
    },
    {
      key: "checkout",
      label: "Checkout started",
      total: checkout_starts.total,
      sub: `${checkout_starts.chalkboard} Chalkboard · ${checkout_starts.clipboard_plus} Clipboard+`,
      pct: conversion.signup_to_checkout_pct,
      color: "var(--chalk-green)",
    },
    {
      key: "upgrades",
      label: "Upgrades completed",
      total: upgrades.total,
      sub: `${upgrades.chalkboard} Chalkboard · ${upgrades.clipboard_plus} Clipboard+`,
      pct: conversion.checkout_to_upgrade_pct,
      color: "#2E7D52",
    },
  ];

  return (
    <ChalkCard data-testid="funnel-widget">
      <div style={{ display: "grid", gap: 14 }}>
        {stages.map((stg) => (
          <div key={stg.key} data-testid={`funnel-stage-${stg.key}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <div>
                <span style={{ fontSize: 12, color: "var(--chalk-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{stg.label}</span>
                {stg.pct !== null && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: "var(--chalk-green)", fontWeight: 500 }}>
                    {stg.pct}% from prior
                  </span>
                )}
              </div>
              <div style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 22, color: "var(--chalk-board)" }}>
                {stg.total}
              </div>
            </div>
            <div style={{ height: 10, background: "rgba(0,0,0,0.05)", borderRadius: 999, overflow: "hidden" }}>
              <div style={{
                width: w(stg.total), height: "100%", background: stg.color,
                transition: "width 320ms ease",
              }} />
            </div>
            <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 4 }}>{stg.sub}</div>
          </div>
        ))}
        <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "0.5px solid var(--chalk-border)" }}>
          <div style={{ fontSize: 12, color: "var(--chalk-muted)" }}>End-to-end conversion (signup → upgrade)</div>
          <div data-testid="funnel-overall-pct" style={{ fontSize: 14, fontWeight: 500, color: "var(--chalk-green)" }}>
            {conversion.signup_to_upgrade_pct}%
          </div>
        </div>
      </div>
    </ChalkCard>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div style={{
      background: "#fff", border: "0.5px solid var(--chalk-border)",
      borderRadius: 12, padding: 14,
    }}>
      <div style={{ fontSize: 28, fontFamily: "'DM Serif Display', Georgia, serif",
                    color: warn && value > 0 ? "var(--chalk-amber)" : "var(--chalk-board)" }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--chalk-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{label}</div>
    </div>
  );
}

function SmallBtn({ children, onClick, danger, icon, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            style={{
              background: danger ? "#fff" : "var(--chalk-board)",
              color: danger ? "var(--chalk-red)" : "var(--chalk-white)",
              border: danger ? "0.5px solid var(--chalk-red)" : "none",
              padding: "5px 10px", borderRadius: 999, fontSize: 11,
              display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
            }}>
      {icon} {children}
    </button>
  );
}

const statGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginBottom: 12 };
const backBtn = {
  display: "inline-flex", alignItems: "center", gap: 6,
  background: "transparent", color: "var(--chalk-white)",
  border: "0.5px solid rgba(245,240,232,0.2)", padding: "6px 12px", borderRadius: 999,
  fontSize: 12, cursor: "pointer",
};
const tabLinkBtn = {
  background: "#fff", color: "var(--chalk-board)",
  border: "0.5px solid var(--chalk-border)", padding: "6px 14px", borderRadius: 999,
  fontSize: 13, textDecoration: "none",
};
const miniLink = {
  background: "#fff", color: "var(--chalk-board)",
  border: "0.5px solid var(--chalk-border)", padding: "5px 12px", borderRadius: 999,
  fontSize: 12, textDecoration: "none",
};
